import {
	Form,
	FormItem,
	ReorderableList
} from "client/Components";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import {
	AddTilesResponseModel,
	NedapOnsUpdateTileModel,
	NedapOnsDetailsTileModel,
	UpdateNedapOnsTabModel,
	updateNedapOnsTile
} from "client/Api/Workspace/workspace";
import { IFormHandler, useFormHandler } from "client/Forms";
import { IValueInputAttachable } from "client/Forms/attachable";
import { generateTrackingId } from "client/Misc/utils";
import { createValidationConfiguration, IValidationConfiguration } from "client/Validation";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useImperativeHandle,
	useMemo,
	useState
} from "react";
import { newGuid } from "client_legacy/ndaw/guid";
import { ExistingTile } from "../common";
import { EditTileFromData, initialGenericState } from "../EditGenericTile";
import { EditTileHandle } from "../EditTile";
import { EditTileGeneralFields } from "../GeneralFields";
import { useUpdateTile } from "../hooks/useUpdateTile";
import { EditNedapOnsTabItem } from "./EditNedapOnsTabItem";

const MAX_TAB_NUMBER = 20;

type NedapOnsEditFormData = EditTileFromData<NedapOnsUpdateTileModel>;

const validationBuilder: IValidationConfiguration<NedapOnsEditFormData> =
	createValidationConfiguration<NedapOnsEditFormData>();

validationBuilder.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

const tabValidation = validationBuilder.when(
	(formData): boolean =>	{
		const { columnSpan, rowSpan } = formData.dimension;
		return computeAreTabNeeded(columnSpan, rowSpan);
	}
)
	.forCollection("tabs")
	.forItems(
		item => item.uniqueId
	);

tabValidation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

tabValidation.forScalar("resourceKind")
	.add(
		isNotNullNorUndefined()
	);

export type EditNedapOnsTileProps = {
	initialModel: NedapOnsDetailsTileModel;
	mode: ExistingTile;
	onIsLoadingChange(isLoading: boolean): void;
	isLoading: boolean;
	onAfterSave: (response?: AddTilesResponseModel) => void;
};

export const EditNedapOnsTile = forwardRef(
	(props: EditNedapOnsTileProps, ref: Ref<EditTileHandle>): ReactElement => {
		const {
			initialModel,
			mode,
			onIsLoadingChange,
			isLoading,
			onAfterSave,
		} = props;

		const [formData, setFormData] = useState<NedapOnsEditFormData>({
			...initialGenericState(initialModel),
			destinationWindow: initialModel.destinationWindow,
			tabs: initialModel.tabs
		});
		const formHandler: IFormHandler<NedapOnsEditFormData> = useFormHandler(
			formData,
			setFormData,
			validationBuilder
		);

		const { columnSpan, rowSpan } = formHandler.for("dimension")
			.value;

		const areTabsNeeded: boolean = useMemo(
			(): boolean => {
				return computeAreTabNeeded(columnSpan, rowSpan);
			},
			[columnSpan, rowSpan]
		);

		const { updateHandler } = useUpdateTile<NedapOnsUpdateTileModel>(
			formHandler,
			updateNedapOnsTile,
			isLoading,
			onIsLoadingChange,
			mode,
			onAfterSave,
			prepareModelForSaving
		);

		useImperativeHandle(
			ref,
			() => ({
				updateHandler
			})
		);

		const itemMoveHandler = useCallback(
			(fromIndex: number, toIndex: number): void => {
				setFormData(
					(formState) => {
						const tabsCopy: UpdateNedapOnsTabModel[] = [...formState.tabs];
						const item: UpdateNedapOnsTabModel = tabsCopy.splice(fromIndex, 1)[0];
						tabsCopy.splice(toIndex, 0, item);

						return {
							...formState,
							tabs: tabsCopy
						};
					}
				);
			},
			[]
		);

		const addTabHandler = (): void => {
			const newTab: UpdateNedapOnsTabModel = {
				name: "",
				resourceKind: undefined,
				uniqueId: newGuid()
			};
			const tabsHandler: IValueInputAttachable<UpdateNedapOnsTabModel[]> = formHandler.for("tabs");

			tabsHandler.reducer
				.push(newTab);

			tabsHandler.validation
				.triggerInputFinished();
		};

		const removeTabHandler = (index: number): void => {
			const tabsHandler: IValueInputAttachable<UpdateNedapOnsTabModel[]> = formHandler.for("tabs");

			if (formHandler.for("tabs").value.length > 1) {
				tabsHandler.reducer
					.removeAt(index);

				tabsHandler.validation
					.triggerInputFinished();
			}
		};

		return (
			<Form onSubmit="NONE">
				<EditTileGeneralFields
					initialModel={initialModel}
					formHandler={formHandler}
				/>
				<AppDestinationWindowSelectFormItem
					attachable={formHandler.for("destinationWindow")}
				/>
				{areTabsNeeded && (
					<FormItem hasNoMargin>
						<ReorderableList onItemMove={itemMoveHandler}>
							{formHandler.for("tabs")
								.map((tabHandler, index) => (
									<EditNedapOnsTabItem
										key={tabHandler.value
											.uniqueId}
										onRemoveTab={() => removeTabHandler(index)}
										attachable={tabHandler}
									/>
								))
							}
						</ReorderableList>
						{formData.tabs.length < MAX_TAB_NUMBER && (
							<ReorderableList.AddButton
								trackingId={generateTrackingId("edit-nedap-ons-tile", "__add-tab-button")}
								onClick={addTabHandler}
							/>
						)}
					</FormItem>
				)}
			</Form>
		);
	}
);

EditNedapOnsTile.displayName = "EditNedapOnsTile";

function computeAreTabNeeded(columnSpan: number, rowSpan: number): boolean {
	return columnSpan >= 4 && rowSpan >= 4;
}

function prepareModelForSaving(formData: NedapOnsEditFormData): NedapOnsEditFormData {
	const { columnSpan, rowSpan } = formData.dimension;
	const areTabsNeeded = computeAreTabNeeded(columnSpan, rowSpan);

	return {
		...formData,
		tabs: areTabsNeeded ? formData.tabs : []
	};
}